// libraries
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";

import { ALL_WEEKDAYS, ByWeekday, Frequency, RRule, Weekday, WeekdayStr } from "rrule";

// utilities
import { createRRule } from "./createRRule";
import { simpleFreqToRRule } from "./recurrence";
import { dueDateFormToDuration } from "../time";

// types
import { ScheduleCreateForm, ScheduleType } from "../../typings/scheduler/scheduler-types";
import { MonthStrToIndexMap, weekdayStrMap } from "../../typings";

dayjs.extend(utc);
dayjs.extend(tz);

/**
 * Utility functions
 */
function weekdayFromOpt(wd: ByWeekday | string): Weekday {
  if (typeof wd === "number") {
    return new Weekday(wd);
  } else if (typeof wd === "object") {
    return new Weekday((wd as Weekday).weekday);
  } else if (typeof wd === "string") {
    return new Weekday(weekdayStrMap[wd] || 0);
  } else {
    return new Weekday(ALL_WEEKDAYS.indexOf(wd as WeekdayStr));
  }
}

function toNumber(d: string | number | undefined): number | undefined {
  return typeof d == "string" ? (isNaN(Number(d)) ? undefined : Number(d)) : d;
}

/**
 * generate rrule form form values
 */
export function rruleFromForm(
  values: Pick<
    ScheduleCreateForm,
    "startDate" | "_recurrence" | "frequencySimplified" | "timeZone" | "_timeOfDay"
  >,
): { rrule?: RRule; startDate?: dayjs.Dayjs } {
  const _startDate = values.startDate;
  if (!_startDate) {
    return {};
  }

  /*
   * Keep local time because we want to keep the same date the user selected and override the time.
   * The time set in the startDate by default will likely be the current time.
   */
  const startDate = new Date(_startDate);
  const timeZone = values.timeZone;

  // Time field uses the time of day in the local timezone

  const hours = dayjs(values._timeOfDay).tz(timeZone).hour();
  const mins = dayjs(values._timeOfDay).tz(timeZone).minute();

  switch (values.frequencySimplified) {
    case "custom": {
      const { freq, bymonthday, byyearday, bysetpos, interval, bymonth, until, count, _weekday } =
        values._recurrence || {};

      return {
        startDate: dayjs(startDate),
        rrule: createRRule(
          {
            /* this comes back as a string */
            freq: toNumber(freq) ?? Frequency.DAILY,
            dtstart: new Date(startDate),
            ...(count ? { count } : {}),
            ...(interval ? { interval: toNumber(interval) } : {}),
            ...(bysetpos ? { bysetpos: toNumber(bysetpos) } : {}),
            ...(bymonthday ? { bymonthday: bymonthday } : {}),
            ...(byyearday ? { byyearday: byyearday } : {}),
            ...(_weekday ? { byweekday: _weekday.map((w) => weekdayFromOpt(w).weekday) } : {}),
            ...(bymonth ? { bymonth: bymonth?.map((m) => MonthStrToIndexMap[m]) } : {}),
            ...(until ? { until: new Date(until) } : {}),
            byhour: hours,
            byminute: mins,
          },
          timeZone,
        ),
      };
    }
    case "not-repeat":
    case "daily":
    case "weekly":
    case "monthly":
    case "yearly":
    case "weekday": {
      return {
        startDate: dayjs(startDate),
        rrule: simpleFreqToRRule(startDate, values.frequencySimplified, {
          timeZone,
          hours,
          mins,
        }),
      };
    }
  }
  return { startDate: dayjs(startDate), rrule: undefined };
}
/**
 * generate create schedule object from create schedule form
 */
export function createScheduleFormToScheduleType(
  _formValues: Partial<ScheduleCreateForm>,
): Omit<ScheduleType, "id"> | undefined {
  /**
   * @todo all of the validation should be handle in the form validation
   */
  if (!_formValues.name || !_formValues.taskPayload) {
    return;
  }

  if (_formValues.frequencySimplified === "custom" && !_formValues._recurrence) {
    return;
  }

  if (!_formValues._timeOfDay || !_formValues.timeZone) {
    return;
  }

  const { startDate, _timeOfDay, timeZone, _recurrence, frequencySimplified } = _formValues;

  const { rrule, startDate: nextStartDate } = rruleFromForm({
    timeZone,
    startDate,
    frequencySimplified,
    _timeOfDay,
    _recurrence,
  });

  if (!rrule) {
    return;
  }

  return {
    name: _formValues.name,
    end: _formValues.end,
    startDate: nextStartDate ? nextStartDate.toDate() : new Date(),
    nextScheduledDate: null,
    pastScheduledRuns: [],
    timeZone: _formValues.timeZone,
    recurrence: rrule.toString(),
    taskType: _formValues.taskType || "form",
    taskPayload: {
      ..._formValues.taskPayload,
      dueDate: dueDateFormToDuration(_formValues),
    },
    paused: false,
    pausedUntil: null,
    /**
     * @todo store enough informaiton so that the edit form dosn't rely on the rrule library
     * or anything really
     */
    // form specific properties
    frequencySimplified: _formValues.frequencySimplified,
  };
}
